import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, Toast, ToastContainer, Dropdown, ButtonGroup } from 'react-bootstrap';
import { useCompetition } from './CompetitionContext';
import { useAuth } from './auth/AuthContext';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../css/competition-admin.css';

const labelClass = { color: "#555", fontSize: 14, fontWeight: 700, marginBottom: 7 };

const MEETING_SCOPED_BASE_PATHS = new Set([
    'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
    'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
    'system-settings', 'registration-desk', 'racing-dashboard',
    'commentary', 'club-summary'
]);

const TIMETABLE_EXPORT_KEYS = new Set(['timetable-standard', 'timetable-officials', 'timetable-print-programme']);

function buildMeetingScopedPath(pathname) {
    const currentPathname = String(window.location.pathname || '');
    const currentSearch = String(window.location.search || '');
    const currentSegments = currentPathname.split('/').filter(Boolean);
    const activeSlug = (currentSegments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(currentSegments[1]))
        ? String(currentSegments[0] || '').trim().toLowerCase()
        : '';

    const targetSegments = String(pathname || '').split('/').filter(Boolean);
    const targetIsMeetingScoped = targetSegments.length >= 1 && MEETING_SCOPED_BASE_PATHS.has(targetSegments[0]);
    const scopedPathname = (activeSlug && targetIsMeetingScoped)
        ? `/${activeSlug}/${targetSegments.join('/')}`
        : pathname;

    const params = new URLSearchParams(currentSearch);
    const rawMeetingId = String(params.get('meetingId') || '').trim();
    const meetingId = Number.parseInt(rawMeetingId, 10);
    const meetingSearch = Number.isFinite(meetingId) && meetingId > 0 ? `?meetingId=${meetingId}` : '';

    return `${scopedPathname}${meetingSearch}`;
}

function formatLaneForExport(race, lane) {
    const title = String(lane?.title || '').trim();
    const crew = String(lane?.suffix || '').trim();
    const mastersCategory = String(lane?.mastersCategory || '').trim().toUpperCase();
    const categoryTag = race?.event?.handicapEvent && mastersCategory ? ` [Mas${mastersCategory}]` : '';

    const club = crew ? `${title} (${crew})${categoryTag}`.trim() : `${title}${categoryTag}`.trim();

    const number = String(lane?.competitorNo || '').trim();
    if (!number) return club;
    if (!club) return number;
    if (/^(TBC|BYE)$/i.test(club)) return club;
    if (club.startsWith(`${number} - `)) return club;
    return `${number} - ${club}`;
}

function formatProgrammeTime(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value) return '';

    const hhmmss = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (hhmmss) {
        const hh = String(Number(hhmmss[1] || 0)).padStart(2, '0');
        const mm = hhmmss[2];
        return `${hh}:${mm}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    const hh = String(parsed.getHours()).padStart(2, '0');
    const mm = String(parsed.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}

function parseProgrammeDate(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const british = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (british) {
        const day = Number(british[1]);
        const month = Number(british[2]);
        const year = Number(british[3]);
        return { day, month, year };
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;

    return {
        day: parsed.getDate(),
        month: parsed.getMonth() + 1,
        year: parsed.getFullYear()
    };
}

function parseMeetingDateStart(value) {
    const parsed = parseProgrammeDate(value);
    if (!parsed) return null;

    const candidate = new Date(parsed.year, parsed.month - 1, parsed.day, 0, 0, 0, 0);
    if (Number.isNaN(candidate.getTime())) return null;
    return candidate;
}

function buildProgrammeFilename(meetingPayload) {
    const parsed = parseProgrammeDate(meetingPayload?.meetingDate || meetingPayload?.competitionDate);
    const now = new Date();
    const month = parsed?.month || now.getMonth() + 1;
    const day = parsed?.day || now.getDate();
    const year = parsed?.year || now.getFullYear();
    return `timetable_for_prog7_${month}_${day}_${year}.tsv`;
}

function parseSubmittedTimestamp(value) {
    const raw = String(value || '').trim();
    if (!raw) return 0;

    const british = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)?(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (british) {
        const day = Number(british[1]);
        const month = Number(british[2]);
        const year = Number(british[3]);
        const hour = Number(british[4]);
        const minute = Number(british[5]);
        const second = Number(british[6] || 0);
        return new Date(year, month - 1, day, hour, minute, second).getTime();
    }

    const britishDateOnly = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (britishDateOnly) {
        const day = Number(britishDateOnly[1]);
        const month = Number(britishDateOnly[2]);
        const year = Number(britishDateOnly[3]);
        return new Date(year, month - 1, day, 0, 0, 0).getTime();
    }

    const direct = Date.parse(raw);
    if (Number.isFinite(direct)) return direct;

    return 0;
}

function parseCriValue(entry) {
    const raw = entry?.cri ?? entry?.CRI ?? null;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : Number.NEGATIVE_INFINITY;
}

function parseClubSortValue(entry) {
    return String(
        entry?.titleMobile
        || entry?.titleAbbreviated
        || entry?.clubshortName
        || entry?.clubShortName
        || entry?.club
        || entry?.clubName
        || ''
    )
        .trim()
        .toLocaleLowerCase();
}

function toDotNetDateTicks(dateValue) {
    const text = String(dateValue || '').trim();
    if (!text) return '';

    let utcMillis = NaN;

    // Prefer explicit date-only parsing to avoid locale drift.
    const isoDateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
        const year = Number(isoDateOnly[1]);
        const month = Number(isoDateOnly[2]);
        const day = Number(isoDateOnly[3]);
        utcMillis = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
    } else {
        const parsed = new Date(text);
        if (!Number.isNaN(parsed.getTime())) {
            utcMillis = parsed.getTime();
        }
    }

    if (!Number.isFinite(utcMillis)) return '';

    const unixMs = Math.trunc(utcMillis);
    if (!Number.isFinite(unixMs) || unixMs < 0) return '';

    const unixTicks = `${unixMs}0000`;
    return addDecimalStrings(unixTicks, '621355968000000000');
}

function addDecimalStrings(a, b) {
    const left = String(a || '').replace(/\D/g, '') || '0';
    const right = String(b || '').replace(/\D/g, '') || '0';

    let i = left.length - 1;
    let j = right.length - 1;
    let carry = 0;
    let out = '';

    while (i >= 0 || j >= 0 || carry > 0) {
        const av = i >= 0 ? left.charCodeAt(i) - 48 : 0;
        const bv = j >= 0 ? right.charCodeAt(j) - 48 : 0;
        const sum = av + bv + carry;
        out = String(sum % 10) + out;
        carry = Math.floor(sum / 10);
        i -= 1;
        j -= 1;
    }

    return out.replace(/^0+(?=\d)/, '');
}

function buildBroeCrewUrl(crewId, dateTicks) {
    const safeId = Number(crewId || 0);
    const safeTicks = String(dateTicks || '').trim();
    if (!safeId || !safeTicks) return '';
    return `https://broe2.britishrowing.org/CompetitionAdmin/ViewCrew/139?id2=${encodeURIComponent(safeTicks)}&crewId=${safeId}`;
}

// Utility for sorting (handles string/number/date columns)
const getSortFn = (key, direction) => (a, b) => {
    let av = a[key], bv = b[key];

    if (key === "cri") {
        const aCri = parseCriValue(a);
        const bCri = parseCriValue(b);
        return direction === "asc" ? aCri - bCri : bCri - aCri;
    }

    if (key === "clubshortName") {
        const aClub = parseClubSortValue(a);
        const bClub = parseClubSortValue(b);
        const byClub = aClub.localeCompare(bClub, undefined, { numeric: true, sensitivity: 'base' });
        if (byClub !== 0) return direction === "asc" ? byClub : -byClub;

        // Stable fallback when club names are equal.
        const aName = String(a?.name || '').toLocaleLowerCase();
        const bName = String(b?.name || '').toLocaleLowerCase();
        const byName = aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
        return direction === "asc" ? byName : -byName;
    }

    // Numerical sort fallback for undefined
    if (typeof av === "number" && typeof bv === "number") {
        return direction === "asc" ? av - bv : bv - av;
    }

    // Date sort (robust to ISO and localized en-GB strings)
    if (key === "dateSubmitted") {
        const aTs = parseSubmittedTimestamp(av);
        const bTs = parseSubmittedTimestamp(bv);
        return direction === "asc" ? aTs - bTs : bTs - aTs;
    }

    // String sort
    av = String(av || ""); bv = String(bv || "");
    return direction === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
};

// Rank an event's gender from its BROE-style code (e.g. "Op.MasA.4x").
// Order: open, women's, mixed, then anything else.
const genderRankFromEventName = (eventName) => {
    const token = String(eventName || '').split('.')[0].trim().toLowerCase().replace(/[^a-z]/g, '');
    if (token === 'op' || token === 'o' || token === 'open' || token === 'x') return 0;
    if (token === 'w' || token === 'women' || token === 'womens' || token === 'woman' || token === 'f' || token === 'female') return 1;
    if (token === 'mx' || token === 'mixed') return 2;
    return 3;
};

// Rank an event's category from its BROE-style code.
// Order: no category (senior/open), masters, junior, then anything else.
const categoryRankFromEventName = (eventName) => {
    const parts = String(eventName || '').split('.').map(p => p.trim()).filter(Boolean);
    const category = parts.slice(1, -1).join('').replace(/\s+/g, '');
    if (!category) return 0;
    if (/^mas/i.test(category)) return 1;
    if (/^(?:j|junior|u|under)?\d/i.test(category)) return 2;
    return 3;
};

const compareEventSummaryByEventName = (a, b) => {
    const genderDiff = genderRankFromEventName(a?.eventName) - genderRankFromEventName(b?.eventName);
    if (genderDiff !== 0) return genderDiff;

    const categoryDiff = categoryRankFromEventName(a?.eventName) - categoryRankFromEventName(b?.eventName);
    if (categoryDiff !== 0) return categoryDiff;

    return String(a?.eventName || '').localeCompare(String(b?.eventName || ''), undefined, { numeric: true, sensitivity: 'base' });
};

// Masters categories run oldest (later letter) to youngest (earlier letter).
const MASTERS_CATEGORY_ORDER = 'ABCDEFGHIJ';

function normalizeMastersCategoryLetter(value) {
    const raw = String(value || '').trim().toUpperCase();
    return /^[A-J]$/.test(raw) ? raw : '';
}

// Older masters categories (higher letter) start first; younger start after on handicap.
function compareMastersCategoryLetters(a, b) {
    return MASTERS_CATEGORY_ORDER.indexOf(normalizeMastersCategoryLetter(a)) - MASTERS_CATEGORY_ORDER.indexOf(normalizeMastersCategoryLetter(b));
}

// Shared event-title formatter (matches the timetable/live views).
function formatEventTitle(value) {
    const titleCased = String(value || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return titleCased
        .replace(/\bmas\s*([a-j](?:\s*\/\s*[a-j])*)\b/gi, (_, category) => `Mas${String(category).replace(/\s+/g, '').toUpperCase()}`)
        .replace(/\bmas([a-j](?:\/[a-j])*)\b/gi, (_, category) => `Mas${String(category).toUpperCase()}`)
        .replace(/\b(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)\b/gi, token => token.toUpperCase());
}

function SingleEntriesList({ singleEntries, selectedNames, onSelect }) {

    const grouped = {};
    singleEntries.forEach(e => {
        if (!grouped[e.seats]) grouped[e.seats] = {};
        if (!grouped[e.seats][e.type]) grouped[e.seats][e.type] = [];
        grouped[e.seats][e.type].push(e);
    });
    Object.values(grouped).forEach(types => {
        Object.values(types).forEach(items => items.sort(compareEventSummaryByEventName));
    });

    return (
        <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>Entries By Event</div>
            <div style={{borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%'}}
            />
            <div style={{ maxHeight: 420, height: 420, overflowY: "auto" }}>
                {Object.entries(grouped).map(([seats, types]) =>
                    Object.entries(types).map(([type, items]) => (
                        <div key={seats + type}>
                            <div style={{ fontWeight: "500", fontSize: 12, margin: "7px 0 0 0" }}>
                                <span style={{ width: 20, display: "inline-block" }}>{seats}</span>
                                <span style={{ fontWeight: 400, marginLeft: 5, color: "#888" }}>{type}</span>
                            </div>
                            {items.map((e, i) => (
                                <div
                                    key={`${e.eventName || 'event'}-${e.name || 'entry'}-${i}`}
                                    onClick={() => onSelect(e.eventName)}
                                    style={{
                                        display: "flex",
                                        fontSize: 12,
                                        alignItems: "center",
                                        background: selectedNames.includes(e.eventName) ? "#e6f0fa" : "#fff",
                                        borderBottom: "1px solid #eee",
                                        padding: "2px 0",
                                        cursor: "pointer",
                                    }}>
                                    <div style={{ width: 25 }}></div>
                                    <div
                                        style={{
                                            width: 75,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                        title={e.eventName}
                                    >
                                        {e.eventName}
                                    </div>
                                    <div style={{ width: 38, textAlign: "right", color: "#555" }}>{e.totalCrews}</div>
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
function EntriesList({ entries, sortKey, sortDirection, setSort, broeDateTicks, meetingTitle }) {
    // Sorted copy (avoid mutating original array)
    const sorted = React.useMemo(() => {
        if (!sortKey) return entries;
        const arr = [...entries];
        arr.sort(getSortFn(sortKey, sortDirection));
        return arr;
    }, [entries, sortKey, sortDirection]);

    // Helper to handle sort toggling
    const handleSort = (key) => {
        if (sortKey === key) {
            setSort(key, sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(key, 'asc');
        }
    };

    // Helper to show sort indicator
    const sortIndicator = (key) => {
        if (sortKey !== key) return null;
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const normaliseWhatsAppPhone = (value) => {
        const raw = String(value || '').trim();
        if (!raw) return '';

        let cleaned = raw.replace(/[^\d+]/g, '');
        if (!cleaned) return '';

        if (cleaned.startsWith('00')) cleaned = `+${cleaned.slice(2)}`;

        if (cleaned.startsWith('+')) cleaned = cleaned.slice(1);
        cleaned = cleaned.replace(/\D/g, '');

        // BROE phone numbers are typically UK mobile format with a leading 0.
        if (/^0\d{9,10}$/.test(cleaned)) {
            cleaned = `44${cleaned.slice(1)}`;
        }

        return cleaned;
    };

    const buildWhatsAppLink = (entry) => {
        const phone = normaliseWhatsAppPhone(entry.preEventContactNumber);
        if (!phone) return '';

        const context = [
            meetingTitle,
            entry.eventName,
            entry.clubshortName || entry.clubShortName || entry.club,
            entry.name ? `Crew ${entry.name}` : ''
        ].filter(Boolean).join(' | ');
        const greeting = entry.preEventContactName ? `Hi ${entry.preEventContactName},` : 'Hi,';
        const message = `${greeting} this is about ${context}.`;
        return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    };

    return (
        <div style={{ flex: "2 1 0", minWidth: 0, border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>Entries</div>
            <div style={{ fontSize: 11, color: '#8a4b08', marginTop: -4 }}>
                <strong>x2</strong> means every competitor in that crew is also entered in another event.
            </div>
            <div style={{ fontSize: 11, color: '#a02828', marginTop: 2 }}>
                <strong>!</strong> means one or more competitors in that crew has a missing membership number.
            </div>
            <div style={{borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%'}} />
            <div style={{ maxHeight: 690, overflowY: "auto" }}>
                <table className="table align-middle mb-0 entriesList" style={{ background: '#fff' }}>
                    <thead>
                        <tr style={{ fontSize: 12, color: '#555' }}>
                            <th style={{ width: 'auto', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('clubshortName')}>Club{sortIndicator('clubshortName')}</th>
                            <th style={{ width: 105, cursor: 'pointer' }} onClick={() => handleSort('eventName')}>Event Name{sortIndicator('eventName')}</th>
                            <th style={{ width: 160, cursor: 'pointer' }} onClick={() => handleSort('name')}>Crew Name{sortIndicator('name')}</th>
                            <th style={{ width: 120, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('dateSubmitted')}>Submitted{sortIndicator('dateSubmitted')}</th>
                            <th style={{ width: 62, cursor: 'pointer' }} onClick={() => handleSort('cri')}>CRI{sortIndicator('cri')}</th>
                            <th style={{ width: 70, whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => handleSort('band')}>Band{sortIndicator('band')}</th>
                            <th style={{ width: 58, whiteSpace: 'nowrap' }} aria-label="Notes and actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(entry => (
                            <tr key={entry.id} style={{ fontSize: 12, color: entry.status === 4 ? '#aaa' : '#222', fontStyle: entry.status === 4 ? 'italic' : undefined }}>
                                <td style={{ fontWeight: entry.status === 4 ? undefined : 500, minWidth: 80, padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                    {buildBroeCrewUrl(entry?.id, broeDateTicks) ? (
                                        <a
                                            href={buildBroeCrewUrl(entry.id, broeDateTicks)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open in BROE"
                                            style={{ color: 'inherit', textDecoration: 'underline' }}
                                        >
                                            {entry.competitorNo != null && entry.competitorNo !== '' ? `${entry.competitorNo} - ` : ''}
                                            {entry.titleMobile || entry.titleAbbreviated || entry.clubshortName || entry.clubShortName || ''}
                                            {entry.status === 4 && <span> (scr)</span>}
                                        </a>
                                    ) : (
                                        <>
                                            {entry.competitorNo != null && entry.competitorNo !== '' ? `${entry.competitorNo} - ` : ''}
                                            {entry.titleMobile || entry.titleAbbreviated || entry.clubshortName || entry.clubShortName || ''}
                                            {entry.status === 4 && <span> (scr)</span>}
                                        </>
                                    )}
                                </td>
                                <td
                                    style={{
                                        width: 105,
                                        maxWidth: 105,
                                        padding: '4px 6px',
                                        overflow: 'hidden'
                                    }}
                                    title={entry.eventName}
                                >
                                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {entry.eventName}
                                        {entry.avgAge != null && (entry.submittedMastersCategory || /\bMas/i.test(entry.eventName || '')) && (
                                            <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>{entry.avgAge}</span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', padding: '4px 6px' }}>
                                    <span>{entry.name}</span>
                                    {entry.hasMissingMembershipNumber && (
                                        <span
                                            title="One or more competitors in this crew has a missing membership number"
                                            aria-label="One or more competitors in this crew has a missing membership number"
                                            style={{
                                                display: 'inline-block',
                                                marginLeft: 6,
                                                fontSize: 10,
                                                lineHeight: '14px',
                                                padding: '0 4px',
                                                borderRadius: 8,
                                                border: '1px solid #e07a7a',
                                                background: '#fff1f1',
                                                color: '#a02828',
                                                fontWeight: 700,
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            !
                                        </span>
                                    )}
                                    {entry.isFullyDoubledUp && (
                                        <span
                                            title={[
                                                'All competitors in this crew are entered in another event:',
                                                ...((entry.doubledUpIntoCrews || []).map(item => `${item.eventName || 'Unknown event'} - ${item.crewName || 'Unknown crew'}`))
                                            ].join('\n')}
                                            aria-label="All competitors in this crew are entered in another event"
                                            style={{
                                                display: 'inline-block',
                                                marginLeft: 6,
                                                fontSize: 10,
                                                lineHeight: '14px',
                                                padding: '0 4px',
                                                borderRadius: 8,
                                                border: '1px solid #f3b067',
                                                background: '#fff3df',
                                                color: '#8a4b08',
                                                fontWeight: 700,
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            x2
                                        </span>
                                    )}
                                </td>
                                <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{entry.dateSubmitted?.split(':').slice(0, 2).join(':') || entry.dateSubmitted}</td>
                                <td style={{ padding: '4px 6px' }}>{entry.cri ?? entry.CRI}</td>
                                <td style={{ width: 70, whiteSpace: 'nowrap', padding: '4px 6px' }}>{entry.band}</td>
                                <td style={{ width: 82, padding: '4px 2px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                   {entry.notes && entry.notes.trim() && (
                                        <span
                                            title={entry.notes}
                                            aria-label={`Notes: ${entry.notes}`}
                                            style={{ color: '#888', marginRight: 4, display: 'inline-flex', alignItems: 'center', cursor: 'default' }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5M5 8a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1z"/>
                                                <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                                            </svg>
                                        </span>
                                    )}
                                    {entry.preEventContactEmail && (
                                        <a
                                            href={`mailto:${entry.preEventContactEmail}?subject=${encodeURIComponent([meetingTitle, entry.clubshortName || entry.clubShortName || '', entry.eventName].filter(Boolean).join(' '))}`}
                                            title={`Email: ${entry.preEventContactEmail}`}
                                            aria-label={`Send email to ${entry.preEventContactEmail}`}
                                            style={{ color: '#555', marginRight: 4, display: 'inline-flex', alignItems: 'center' }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z"/>
                                            </svg>
                                        </a>
                                    )}
                                    {buildWhatsAppLink(entry) && (
                                        <a
                                            href={buildWhatsAppLink(entry)}
                                            title={`WhatsApp: ${entry.preEventContactNumber}`}
                                            aria-label={`Send WhatsApp message to ${entry.preEventContactNumber}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#25D366', marginRight: 4, display: 'inline-flex', alignItems: 'center' }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M13.601 2.326A7.85 7.85 0 0 0 8.03 0C3.683 0 .143 3.54.143 7.886c0 1.39.363 2.747 1.052 3.944L0 16l4.278-1.122a7.9 7.9 0 0 0 3.752.954h.003c4.347 0 7.887-3.54 7.887-7.886a7.85 7.85 0 0 0-2.319-5.62zM8.034 14.56h-.003a6.57 6.57 0 0 1-3.35-.92l-.24-.142-2.538.666.677-2.476-.156-.254a6.56 6.56 0 0 1-1.007-3.5c0-3.627 2.95-6.577 6.58-6.577 1.756 0 3.406.684 4.647 1.924a6.54 6.54 0 0 1 1.928 4.655c-.002 3.627-2.952 6.577-6.582 6.577z"/>
                                                <path d="M11.696 9.958c-.2-.1-1.183-.583-1.367-.65-.183-.067-.317-.1-.45.1-.133.2-.516.65-.633.783-.116.133-.233.15-.433.05-.2-.1-.843-.31-1.605-.99-.592-.528-.992-1.18-1.108-1.38-.117-.2-.012-.308.087-.407.09-.09.2-.233.3-.35.1-.117.133-.2.2-.333.067-.133.033-.25-.017-.35-.05-.1-.45-1.083-.616-1.483-.162-.39-.327-.337-.45-.343l-.383-.007c-.133 0-.35.05-.533.25-.183.2-.7.683-.7 1.666 0 .983.717 1.933.817 2.066.1.133 1.41 2.153 3.417 3.018.477.206.85.33 1.14.423.479.152.915.13 1.259.079.384-.057 1.183-.483 1.35-.95.166-.466.166-.866.116-.95-.05-.083-.183-.133-.383-.233z"/>
                                            </svg>
                                        </a>
                                    )}
                                    {entry.preEventContactNumber && (
                                        <a
                                            href={`tel:${entry.preEventContactNumber}`}
                                            title={`Call: ${entry.preEventContactNumber}`}
                                            aria-label={`Call ${entry.preEventContactNumber}`}
                                            style={{ color: '#555', display: 'inline-flex', alignItems: 'center' }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                                <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/>
                                            </svg>
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
function StraightFinalsList({ straightFinals, selectedNames, onSelect }) {
    const formatCri = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric.toFixed(1).replace(/\.0$/, '') : '—';
    };

    return (
        <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>Straight Finals</div>
            <div style={{borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%'}} />
            <div style={{ maxHeight: 420, height: 420, overflowY: "auto" }}>
                {straightFinals.map((e, i) => (
                    <div
                        key={`${e.eventName || 'event'}-${e.name || 'entry'}-${i}`}
                        onClick={() => onSelect(e.eventName)}
                        style={{
                            display: "flex",
                            fontSize: 12,
                            alignItems: "center",
                            background: e.sameClub ? "#ffcccc" : (selectedNames.includes(e.eventName) ? "#e6f0fa" : "#fff"),
                            borderBottom: "1px solid #eee",
                            padding: "2px 2px",
                            cursor: "pointer",
                        }}
                    >
                        <div
                            style={{
                                width: 125,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            title={e.eventName}
                        >
                            {e.eventName}
                        </div>
                        <div
                            style={{
                                width: 92,
                                textAlign: 'right',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 4,
                                fontVariantNumeric: 'tabular-nums'
                            }}
                            title={Array.isArray(e.crewCriScores) ? e.crewCriScores.map(formatCri).join(' / ') : ''}
                        >
                            {e.criMismatch && (
                                <span
                                    style={{
                                        color: '#7a4e00',
                                        background: '#fff3cd',
                                        border: '1px solid #f2d48a',
                                        borderRadius: 10,
                                        fontSize: 9,
                                        lineHeight: 1,
                                        fontWeight: 700,
                                        padding: '2px 5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.2
                                    }}
                                    title={Number.isFinite(Number(e.criStandardDeviation))
                                        ? `High CRI spread (std dev ${Number(e.criStandardDeviation).toFixed(2)})`
                                        : 'High CRI spread'}
                                >
                                    Warn
                                </span>
                            )}
                            <span>{Array.isArray(e.crewCriScores) ? e.crewCriScores.map(formatCri).join(' / ') : '—'}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
function TotalPrizesBox({ prizes }) {
    return <div>{prizes}</div>;
}

// Scratched Entries List
function ScratchedEntriesList({ entries, allEntries, selectedNames, onSelect }) {
    // Calculate remaining crews (status 1) for each event
    const remainingCrewsByEvent = React.useMemo(() => {
        const eventCounts = {};
        allEntries.filter(e => e.status === 1).forEach(entry => {
            eventCounts[entry.eventName] = (eventCounts[entry.eventName] || 0) + 1;
        });
        return eventCounts;
    }, [allEntries]);

    return (
        <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>Scratched Entries ({entries.length})</div>
            <div style={{borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%'}} />
            <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                <table className="table align-middle mb-0 entriesList" style={{ background: '#fff' }}>
                    <thead>
                        <tr style={{ fontSize: 12, color: '#555', background: '#ccc' }}>
                            <th style={{ width: 180 }}>Club</th>
                            <th style={{ width: 85, cursor: 'pointer' }}>Event</th>
                            <th style={{ width: 90 }}>Crew Name</th>
                            <th style={{ width: 70, textAlign: 'center' }}>Remaining</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(entry => {
                            const remaining = remainingCrewsByEvent[entry.eventName] || 0;
                            const highlight = remaining < 2;

                            const rowStyle = {
                                fontSize: 12,
                                color: highlight ? 'red' : '#222',
                                fontWeight: highlight ? 'bold' : 'normal',
                                fontStyle: 'normal',
                                minHeight: 27,
                                background: selectedNames.includes(entry.eventName) ? "#e6f0fa" : "#fff"
                            };

                            return (
                                <tr
                                    key={entry.id}
                                    style={rowStyle}
                                >
                                    <td style={{ width: 180, padding: '4px 6px' }}>
                                        {entry.competitorNo != null && entry.competitorNo !== '' ? `${entry.competitorNo} - ` : ''}{entry.titleMobile || entry.titleAbbreviated || entry.clubshortName || entry.clubShortName || ''}
                                    </td>
                                    <td
                                        style={{
                                            width: 85,
                                            padding: '4px 6px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => onSelect(entry.eventName)}
                                    >
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                verticalAlign: 'bottom'
                                            }}
                                            title={entry.eventName}
                                        >
                                            {entry.eventName}
                                        </span>
                                    </td>
                                    <td style={{ width: 90, padding: '4px 6px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{entry.name}</td>
                                    <td style={{ width: 70, padding: '4px 6px', textAlign: 'center' }}>
                                        {remaining}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NoCompetitionList({ entries, selectedNames, onSelect }) {
    return (
        <div style={{ marginTop: 24, border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>No Competition (Submitted) ({entries.length})</div>
            <div style={{borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%'}} />
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table className="table align-middle mb-0 entriesList" style={{ background: '#fff' }}>
                    <thead>
                        <tr style={{ fontSize: 12, color: '#555', background: '#ccc' }}>
                            <th style={{ width: 180 }}>Club</th>
                            <th style={{ width: 90 }}>Event</th>
                            <th style={{ width: 90 }}>Crew Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(entry => (
                            <tr
                                key={entry.id}
                                style={{
                                    fontSize: 12,
                                    color: '#222',
                                    minHeight: 27,
                                    background: selectedNames.includes(entry.eventName) ? '#e6f0fa' : '#fff'
                                }}
                            >
                                <td style={{ width: 180, padding: '4px 6px' }}>
                                    {entry.competitorNo != null && entry.competitorNo !== '' ? `${entry.competitorNo} - ` : ''}
                                    {entry.titleMobile || entry.titleAbbreviated || entry.clubshortName || entry.clubShortName || ''}
                                </td>
                                <td
                                    style={{ width: 90, padding: '4px 6px', cursor: 'pointer' }}
                                    onClick={() => onSelect(entry.eventName)}
                                >
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            verticalAlign: 'bottom'
                                        }}
                                        title={entry.eventName}
                                    >
                                        {entry.eventName}
                                    </span>
                                </td>
                                <td style={{ width: 90, padding: '4px 6px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                    {entry.name}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CombinedMastersEventsList({ events, selectedNames, onSelect }) {
    return (
        <div style={{ marginTop: 24, border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
            <div style={labelClass}>Combined Masters Events ({events.length})</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: -4 }}>
                Events with crews from more than one masters category race together on handicap.
            </div>
            <div style={{ borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%' }} />
            {events.length === 0 ? (
                <div style={{ fontSize: 12, color: '#888', padding: '6px 2px' }}>No combined masters events.</div>
            ) : (
                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                    {events.map(evt => {
                        const knownCrews = evt.declaredCategories.reduce((sum, cat) => sum + (evt.crewCountByCategory[cat] || 0), 0);
                        const otherCrews = evt.totalCrews - knownCrews;

                        return (
                            <div
                                key={evt.eventName}
                                onClick={() => onSelect(evt.eventName)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '6px 4px',
                                    borderBottom: '1px solid #eee',
                                    background: selectedNames.includes(evt.eventName) ? '#e6f0fa' : '#fff'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span
                                        style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        title={formatEventTitle(evt.eventTitle)}
                                    >
                                        {formatEventTitle(evt.eventTitle)}
                                    </span>
                                    <span style={{ fontSize: 12, color: '#555', marginLeft: 8, whiteSpace: 'nowrap' }}>
                                        {evt.totalCrews} {evt.totalCrews === 1 ? 'crew' : 'crews'}
                                    </span>
                                </div>
                                <div style={{ fontSize: 11, color: '#555', marginTop: 3 }}>
                                    {evt.declaredCategories.map((cat, i) => (
                                        <React.Fragment key={cat}>
                                            <span>
                                                Mas{cat}
                                                <span style={{ color: '#888' }}> ({evt.crewCountByCategory[cat] || 0})</span>
                                            </span>
                                            {i < evt.declaredCategories.length - 1 && <span style={{ color: '#aaa' }}> &middot; </span>}
                                        </React.Fragment>
                                    ))}
                                    {otherCrews > 0 && (
                                        <span style={{ color: '#a02828' }}> &middot; +{otherCrews} uncategorised</span>
                                    )}
                                </div>
                                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                                    <span style={{ color: '#888' }}>
                                        {evt.raceNumbers.length
                                            ? `${evt.raceNumbers.length === 1 ? 'Race' : 'Races'}: ${evt.raceNumbers.join(', ')}`
                                            : 'No races drawn yet'}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function CompetitionAdmin({ view = 'competition' }) {
    const { RunDrawAvailable, currentSettingsId, refreshCompetition } = useCompetition();
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [runningDraw, setRunningDraw] = useState(false);
    const [publicPagesLocked, setPublicPagesLocked] = useState(false);
    const [savingPublicLock, setSavingPublicLock] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [savingReadOnly, setSavingReadOnly] = useState(false);
    const [drawModes, setDrawModes] = useState({ drawModeEnabled: false, reDrawModeEnabled: false });
    const [savingDrawModes, setSavingDrawModes] = useState(false);
    const [savingTiming, setSavingTiming] = useState(false);
    const [broeMeetingIdentifier, setBroeMeetingIdentifier] = useState('');
    const [savingBroeIdentifier, setSavingBroeIdentifier] = useState(false);
    const [urlSlug, setUrlSlug] = useState('');
    const [savingUrlSlug, setSavingUrlSlug] = useState(false);
    const [settingsRows, setSettingsRows] = useState([]);
    const [timingSettings, setTimingSettings] = useState({
        raceIntervalAM: 3,
        raceIntervalPM: 3,
        racesBetweenRounds: 7,
        spareRaceInterval: 0
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [entries, setEntries] = useState([]);
    const [singleEntries, setSingleEntries] = useState([]);
    const [straightFinals, setStraightFinals] = useState([]);
    const [prizes, setPrizes] = useState(0);
    const [timetableRaces, setTimetableRaces] = useState([]);
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [tablePrefsLoaded, setTablePrefsLoaded] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showToastDraw, setShowToastDraw] = useState(false);
    const [competitionAdminStats, setCompetitionAdminStats] = useState({
        added: 0,
        updated: 0,
        totalCrews: 0,
        liveCrews: 0,
        nonWithdrawnUniqueCompetitors: 0,
        missingEvents: [],
        raceCount: 0,
        raceFirst: '',
        raceLast: '',
        emptyRaces: 0,
    });

    const { isAdmin, user, getToken } = useAuth();

    const currentMeeting = useMemo(() => {
        if (!Array.isArray(settingsRows) || settingsRows.length === 0) return null;
        return settingsRows.find((row) => Number(row.id) === Number(currentSettingsId || 0))
            || settingsRows.find((row) => row.isCurrent)
            || settingsRows[0]
            || null;
    }, [currentSettingsId, settingsRows]);

    const entriesClosed = useMemo(() => {
        return String(currentMeeting?.broeRegattaStatus || '').toLowerCase().includes('entries closed');
    }, [currentMeeting]);

    const meetingTitle = useMemo(() => {
        if (!currentMeeting) return 'Competition Admin';
        return String(currentMeeting.regattaName || 'Competition Admin').trim();
    }, [currentMeeting]);

    const meetingSubtitle = useMemo(() => {
        if (!currentMeeting) return '';
        const year = String(currentMeeting.year || '').trim();
        const slug = String(currentMeeting.urlSlug || '').trim();
        const parts = [];
        if (year) parts.push(year);
        if (slug) parts.push(slug);
        return parts.join(' • ');
    }, [currentMeeting]);

    const broeDateTicks = useMemo(() => {
        return toDotNetDateTicks(currentMeeting?.regattaDate || currentMeeting?.meetingDate || currentMeeting?.competitionDate);
    }, [currentMeeting]);

    const applyTimetableStats = useCallback((races) => {
        const safeRaces = Array.isArray(races) ? races : [];
        setTimetableRaces(safeRaces);
        const populatedRaces = safeRaces.filter((race) => Number(race?.event?.id || 0) > 0);
        const orderedRaces = [...populatedRaces].sort((a, b) => new Date(a?.raceTime || 0).getTime() - new Date(b?.raceTime || 0).getTime());
        const firstRace = orderedRaces[0];
        const lastRace = orderedRaces[orderedRaces.length - 1];

        setCompetitionAdminStats((prev) => ({
            ...prev,
            raceCount: populatedRaces.length,
            raceFirst: String(firstRace?.raceTimeFormatted || firstRace?.raceTime || '').trim(),
            raceLast: String(lastRace?.raceTimeFormatted || lastRace?.raceTime || '').trim(),
            emptyRaces: safeRaces.filter((race) => Number(race?.event?.id || 0) <= 0).length,
        }));
    }, []);

    const fetchData = useCallback(async (endpoint) => {
        const token = getToken();
        const headers = { Authorization: `Bearer ${token}` };
        if (currentSettingsId) headers['x-meeting-id'] = String(currentSettingsId);
        const response = await fetch(`/api/${endpoint}`, {
            credentials: 'include',
            headers,
        });
        if (response.status === 401 || response.status === 403) {
            setErrorMessage('Auth error. Please logout and login again.');
            throw new Error('Authentication error');
        }
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    }, [getToken, currentSettingsId]);

    const fetchSettingsRows = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/meeting', {
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }
            if (!response.ok) throw new Error(`Failed to load meeting years (${response.status})`);
            const rows = await response.json();
            const safeRows = Array.isArray(rows) ? rows : [];
            setSettingsRows(safeRows);
            const currentRow = safeRows.find(row => Number(row.id) === Number(currentSettingsId || 0))
                || safeRows.find(row => row.isCurrent);
            if (currentRow) {
                setBroeMeetingIdentifier(String(currentRow.broe2MeetingIdentifier || '').trim());
                setUrlSlug(String(currentRow.urlSlug || '').trim());
                setTimingSettings({
                    raceIntervalAM: Number(currentRow.raceIntervalAM ?? 3),
                    raceIntervalPM: Number(currentRow.raceIntervalPM ?? 3),
                    racesBetweenRounds: Number(currentRow.racesBetweenRounds ?? 7),
                    spareRaceInterval: Number(currentRow.spareRaceInterval ?? 0)
                });
            }
        } catch (err) {
            console.error('Error fetching meeting years:', err);
        }
    }, [currentSettingsId, getToken]);

    const fetchPublicLock = useCallback(async () => {
        try {
            const payload = await fetchData('meeting/public-lock');
            setPublicPagesLocked(Boolean(payload?.publicPagesLocked));
        } catch (err) {
            console.error('Error fetching public lock:', err);
        }
    }, [fetchData]);

    const fetchReadOnly = useCallback(async () => {
        try {
            const payload = await fetchData('meeting/read-only');
            setReadOnly(Boolean(payload?.readOnly));
        } catch (err) {
            console.error('Error fetching read-only flag:', err);
        }
    }, [fetchData]);

    const fetchDrawModes = useCallback(async () => {
        try {
            const payload = await fetchData('meeting/draw-modes');
            setDrawModes({
                drawModeEnabled: Boolean(payload?.drawModeEnabled),
                reDrawModeEnabled: Boolean(payload?.reDrawModeEnabled)
            });
        } catch (err) {
            console.error('Error fetching draw modes:', err);
        }
    }, [fetchData]);
    
    const fetchEntries = useCallback(async () => {
        try {
            setEntries(await fetchData("meeting/entries"));
        } catch (err) {
            console.error('Error fetching entries:', err);
        }
    }, [fetchData]);
    
    const fetchSingleEntries = useCallback(async () => {
        try {
            setSingleEntries(await fetchData("meeting/entries/singles"));
        } catch (err) {
            console.error('Error fetching single entries:', err);
        }
    }, [fetchData]);
    
    const fetchStraightFinals = useCallback(async () => {
        try {
            setStraightFinals(await fetchData("meeting/entries/straight-finals"));
        } catch (err) {
            console.error('Error fetching straight finals:', err);
        }
    }, [fetchData]);
    
    const fetchPrizes = useCallback(async () => {
        try {
            setPrizes(await fetchData("meeting/entries/prizes"));
        } catch (err) {
            console.error('Error fetching prizes:', err);
        }
    }, [fetchData]);

    const fetchUniqueCompetitorCount = useCallback(async () => {
        try {
            const payload = await fetchData('meeting/entries/unique-competitors');
            setCompetitionAdminStats((prev) => ({
                ...prev,
                nonWithdrawnUniqueCompetitors: Number(payload?.totalCompetitors || 0),
            }));
        } catch (err) {
            console.error('Error fetching unique competitor count:', err);
            setCompetitionAdminStats((prev) => ({
                ...prev,
                nonWithdrawnUniqueCompetitors: 0,
            }));
        }
    }, [fetchData]);

    const fetchTimetableSummary = useCallback(async () => {
        try {
            applyTimetableStats(await fetchData('timetable'));
        } catch (err) {
            console.error('Error fetching timetable summary:', err);
            setTimetableRaces([]);
            setCompetitionAdminStats((prev) => ({
                ...prev,
                raceCount: 0,
                raceFirst: '',
                raceLast: '',
                emptyRaces: 0,
            }));
        }
    }, [applyTimetableStats, fetchData]);

    const fetchCompetitionAdminTablePrefs = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/user-preferences/competition-admin', {
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) return;
            const payload = await response.json().catch(() => ({}));
            const payloadSelectedEntries = Array.isArray(payload?.selectedEntries)
                ? payload.selectedEntries.map(value => String(value || '').trim()).filter(Boolean)
                : [];
            const fallbackSelectedEntry = String(payload?.selectedEntry || '').trim();
            const nextSelectedEntries = payloadSelectedEntries.length
                ? payloadSelectedEntries
                : (fallbackSelectedEntry ? [fallbackSelectedEntry] : []);
            const nextSortKey = String(payload?.sortKey || '').trim() || null;
            const nextSortDirection = String(payload?.sortDirection || '').toLowerCase() === 'desc' ? 'desc' : 'asc';
            setSelectedEntries(nextSelectedEntries);
            setSortKey(nextSortKey);
            setSortDirection(nextSortDirection);
        } catch (err) {
            console.error('Error fetching competition admin table preferences:', err);
        } finally {
            setTablePrefsLoaded(true);
        }
    }, [getToken]);

    const persistCompetitionAdminTablePrefs = useCallback(async (prefs) => {
        const token = getToken();
        await fetch('/api/user-preferences/competition-admin', {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(prefs)
        });
    }, [getToken]);
    
    const refreshAll = useCallback(async () => {
        await Promise.all([
            fetchEntries(),
            fetchSingleEntries(),
            fetchStraightFinals(),
            fetchPrizes(),
            fetchUniqueCompetitorCount(),
            fetchPublicLock(),
            fetchReadOnly(),
            fetchDrawModes(),
            fetchTimetableSummary()
        ]);
    }, [fetchEntries, fetchPrizes, fetchSingleEntries, fetchStraightFinals, fetchUniqueCompetitorCount, fetchPublicLock, fetchReadOnly, fetchDrawModes, fetchTimetableSummary]);
    
    useEffect(() => { 
        const loadAllData = async () => {
            await Promise.all([
                fetchEntries(),
                fetchSingleEntries(),
                fetchStraightFinals(),
                fetchPrizes(),
                fetchUniqueCompetitorCount(),
                fetchSettingsRows(),
                fetchPublicLock(),
                fetchReadOnly(),
                fetchDrawModes(),
                fetchTimetableSummary(),
                fetchCompetitionAdminTablePrefs()
            ]);
        };
        loadAllData();
    }, [fetchEntries, fetchSingleEntries, fetchStraightFinals, fetchPrizes, fetchUniqueCompetitorCount, fetchSettingsRows, fetchPublicLock, fetchReadOnly, fetchDrawModes, fetchTimetableSummary, fetchCompetitionAdminTablePrefs]);

    useEffect(() => {
        if (!tablePrefsLoaded) return;
        const timeoutId = window.setTimeout(() => {
            persistCompetitionAdminTablePrefs({
                selectedEntries,
                // Keep legacy field for backward compatibility with older readers.
                selectedEntry: selectedEntries[0] || null,
                sortKey,
                sortDirection
            }).catch((err) => {
                console.error('Error saving competition admin table preferences:', err);
            });
        }, 250);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [selectedEntries, sortKey, sortDirection, tablePrefsLoaded, persistCompetitionAdminTablePrefs]);
    const handleSelectEntry = (name = null) => {
        const safeName = String(name || '').trim();
        if (!safeName) return;
        setSelectedEntries((prev) => {
            if (prev.includes(safeName)) return prev.filter(item => item !== safeName);
            return [...prev, safeName];
        });
    };
    const filteredEntries = selectedEntries.length
        ? entries.filter(e => selectedEntries.includes(e.eventName))
        : entries;
    const clearFilter = useCallback(() => setSelectedEntries([]), []);
    const setSort = (key, direction) => { setSortKey(key); setSortDirection(direction); };
    const loadInfo = useCallback(async () => {
        setLoading(true);
        if (!user || !isAdmin) {
            window.location.href = '/login';
            return;
        }
        try {
            const token = getToken();
            const headers = { Authorization: `Bearer ${token}` };
            if (currentSettingsId) headers['x-meeting-id'] = String(currentSettingsId);
            const response = await fetch('/api/crew', {
                credentials: 'include',
                headers,
            });
            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                setLoading(false);
                return;
            }
            if (!response.ok) throw new Error(`HTTP error: Status ${response.status}`);
            const actualData = await response.json();
            setCompetitionAdminStats((prev) => ({
                ...prev,
                totalCrews: actualData.length,
                liveCrews: actualData.filter((crew) => (crew.status === 1 || crew.status===4)).length,
            }));
        } catch (err) {
            console.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken, isAdmin, user, currentSettingsId]);

    useEffect(() => { loadInfo(); }, [loadInfo]);

    const runDraw = async () => {
        setRunningDraw(true);
        setLoading(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/draw', {
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                setLoading(false);
                return;
            }
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                const reason = payload?.message || `Run draw failed (HTTP ${response.status}).`;
                throw new Error(reason);
            }

            const actualData = await response.json();
            applyTimetableStats(actualData);
            await refreshAll();

            // ✅ Show success toast after successful response + state update
            setShowToastDraw(true);
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Run draw failed.');
        } finally {
            setRunningDraw(false);
            setLoading(false);
        }
    };

    const importCrews = async () => {
        setLoading(true);
        setImporting(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/crew/import', {
                method: 'POST',
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` },
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                const reason = payload?.message || `Import failed (HTTP ${response.status}).`;
                throw new Error(reason);
            }

            const actualData = payload || {};
            setCompetitionAdminStats((prev) => ({
                ...prev,
                liveCrews: actualData.liveCrews ?? prev.liveCrews,
                totalCrews: actualData.totalCrews ?? prev.totalCrews,
            }));
            // 🔁 Re-fetch data after successful import
            await refreshAll();
            // ✅ Show success toast
            setShowToast(true);
        } catch (err) {
            console.error(err.message);
            setErrorMessage(err.message || 'Import failed.');
        } finally {
            setImporting(false);
            setLoading(false);
        }
    };

    const downloadBroeResults = async () => {
        setLoading(true);
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/broeresultsfile', {
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                throw new Error(`HTTP error: Status ${response.status}`);
            }

            const csvText = await response.text();
            const blob = new Blob([csvText], { type: 'text/csv;charset=UTF-8' });

            saveAs(blob, "BROEResults.csv");

        } catch (err) {
            console.error(err.message);
            setErrorMessage('Failed to download BROE results file.');
        } finally {
            setLoading(false);
        }
    };

    const printRaceCards = () => {
        const raceCardsPath = buildMeetingScopedPath('/racecards');
        const newWindow = window.open(
            raceCardsPath,
            'RaceCardsPrintWindow',
            'popup=yes,width=1400,height=900,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no'
        );
        if (!newWindow) {
            window.location.assign(raceCardsPath);
            return;
        }
        newWindow.focus();
    };

    const openDrawPdfExport = () => {
        const exportPath = buildMeetingScopedPath('/draw-export-pdf');
        const newWindow = window.open(
            exportPath,
            'DrawPdfExportWindow',
            'popup=yes,width=1400,height=940,resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no'
        );
        if (!newWindow) {
            window.location.assign(exportPath);
            return;
        }
        newWindow.focus();
    };

    const downloadTimetableExport = async (variant) => {
        const includeResults = variant === 'standard'
            ? window.confirm('Include results?\n\nThis adds Verdict and Winning Time columns and highlights the winner.')
            : false;
        setLoading(true);
        setErrorMessage('');
        try {
            const races = await fetchData('timetable');
            const safeRaces = Array.isArray(races) ? races.filter(race => Number(race?.event?.id || -1) > 0) : [];

            const header = variant === 'officials'
                ? ['No', 'Next', 'Time', 'Event Title', 'Round', 'Enclosure (Middlesex)', 'Surrey', 'Winner', 'Verdict', 'Winning Time']
                : includeResults
                    ? ['No', 'Next', 'Time', 'Event Title', 'Round', 'Enclosure (Middlesex)', 'Surrey', 'Verdict', 'Winning Time']
                    : ['No', 'Next', 'Time', 'Event Title', 'Round', 'Enclosure (Middlesex)', 'Surrey'];

            const winnerLanes = [];
            const rows = safeRaces.map(race => {
                const winner = race?.laneA?.winner
                    ? formatLaneForExport(race, race.laneA)
                    : race?.laneB?.winner
                        ? formatLaneForExport(race, race.laneB)
                        : '';

                winnerLanes.push(race?.laneA?.winner ? 'A' : race?.laneB?.winner ? 'B' : null);

                const base = [
                    race.raceNo,
                    race.nextRaceNo,
                    formatProgrammeTime(race?.raceTimeFormatted || race?.raceTime || ''),
                    race.event.title,
                    race.round,
                    formatLaneForExport(race, race.laneA),
                    formatLaneForExport(race, race.laneB)
                ];

                if (variant === 'officials') {
                    return [...base, winner, race.verdict || '', race.winningTime || ''];
                }

                if (includeResults) {
                    return [...base, race.verdict || '', race.winningTime || ''];
                }

                return base;
            });

            const columnWidths = variant === 'officials'
                ? [5, 5, 8, 30, 8, 42, 42, 26, 16, 14]
                : includeResults
                    ? [5, 5, 8, 30, 8, 46, 46, 16, 14]
                    : [5, 5, 8, 30, 8, 46, 46];

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Timetable', {
                views: [{ state: 'frozen', ySplit: 1 }]
            });

            worksheet.columns = header.map((title, index) => ({
                header: title,
                width: columnWidths[index] || 12
            }));

            worksheet.addRows(rows);

            const headerRow = worksheet.getRow(1);
            headerRow.font = { bold: true };
            headerRow.height = 20;

            if (variant === 'officials') {
                worksheet.eachRow((row, rowNumber) => {
                    if (rowNumber > 1) row.height = 59.5;
                    row.eachCell({ includeEmpty: true }, cell => {
                        cell.alignment = { ...(cell.alignment || {}), vertical: 'top' };
                    });
                });
                headerRow.alignment = { vertical: 'top' };
            }

            if (includeResults) {
                winnerLanes.forEach((lane, index) => {
                    if (!lane) return;
                    const cell = worksheet.getRow(index + 2).getCell(lane === 'A' ? 6 : 7);
                    cell.font = { ...(cell.font || {}), bold: true };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFF2A8' }
                    };
                });
            }

            worksheet.pageSetup = {
                ...(worksheet.pageSetup || {}),
                paperSize: 9,
                orientation: 'landscape',
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 0,
                showGridLines: true
            };

            const meetingYear = String(currentMeeting?.year || '').trim();
            const regattaName = `${String(meetingTitle || '').trim()}${meetingYear ? ` ${meetingYear}` : ''}`.replace(/&/g, '&&');
            worksheet.headerFooter = {
                oddHeader: `&C&B${regattaName}`,
                evenHeader: `&C&B${regattaName}`,
                oddFooter: '&L&D &T&RPage &P',
                evenFooter: '&L&D &T&RPage &P'
            };

            const excelBuffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, variant === 'officials'
                ? 'timetable-officials.xlsx'
                : includeResults
                    ? 'timetable-standard-results.xlsx'
                    : 'timetable-standard.xlsx');
        } catch (err) {
            console.error(err.message);
            setErrorMessage('Failed to download timetable export.');
        } finally {
            setLoading(false);
        }
    };

    const downloadProgrammeTimetableExport = async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const [races, meetingPayload] = await Promise.all([
                fetchData('timetable'),
                fetchData('meeting/current').catch(() => null)
            ]);
            const safeRaces = Array.isArray(races)
                ? races.filter(race => Number(race?.event?.id || -1) > 0)
                : [];

            const sorted = [...safeRaces].sort((a, b) => Number(a?.raceNo || 0) - Number(b?.raceNo || 0));
            const lines = sorted.map(race => {
                const raceNo = race?.raceNo ?? '';
                const raceTime = formatProgrammeTime(race?.raceTimeFormatted || race?.raceTime || '');
                const eventTitle = String(race?.event?.title || '').trim().toUpperCase();
                const laneA = formatLaneForExport(race, race?.laneA);
                const laneB = formatLaneForExport(race, race?.laneB);
                const matchup = `${laneA} v. ${laneB}`.trim();
                return [raceNo, raceTime, eventTitle, matchup].join('\t');
            });

            const tsvText = lines.join('\n');
            const blob = new Blob([tsvText], { type: 'text/tab-separated-values;charset=utf-8' });
            saveAs(blob, buildProgrammeFilename(meetingPayload));
        } catch (err) {
            console.error(err.message);
            setErrorMessage('Failed to download print programme timetable export.');
        } finally {
            setLoading(false);
        }
    };

    const setPublicLock = async (nextLockedValue) => {
        setSavingPublicLock(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/public-lock', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ publicPagesLocked: nextLockedValue })
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message || `Failed to update public page lock (HTTP ${response.status}).`);
            }

            const payload = await response.json().catch(() => ({}));
            setPublicPagesLocked(Boolean(payload.publicPagesLocked));
            await refreshCompetition();
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update public page lock.');
        } finally {
            setSavingPublicLock(false);
        }
    };

    const setReadOnlyFlag = async (nextReadOnlyValue) => {
        setSavingReadOnly(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/read-only', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ readOnly: nextReadOnlyValue })
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message || `Failed to update read-only setting (HTTP ${response.status}).`);
            }

            const payload = await response.json().catch(() => ({}));
            setReadOnly(Boolean(payload.readOnly));
            await refreshCompetition();
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update read-only setting.');
        } finally {
            setSavingReadOnly(false);
        }
    };

    const setDrawModeFlag = async (field, nextValue) => {
        setSavingDrawModes(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/draw-modes', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ [field]: nextValue })
            });

            if (response.status === 401) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message || `Failed to update draw mode setting (HTTP ${response.status}).`);
            }

            setDrawModes({
                drawModeEnabled: Boolean(payload.drawModeEnabled),
                reDrawModeEnabled: Boolean(payload.reDrawModeEnabled)
            });
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update draw mode setting.');
        } finally {
            setSavingDrawModes(false);
        }
    };

    const updateTimingField = async (field, value) => {
        const nextValue = Number(value);
        const nextTimingSettings = {
            ...timingSettings,
            [field]: nextValue
        };

        setTimingSettings(nextTimingSettings);
        setSavingTiming(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/timing', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(nextTimingSettings)
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message || `Failed to update meeting timing (HTTP ${response.status}).`);
            }

            const payload = await response.json().catch(() => ({}));
            setTimingSettings({
                raceIntervalAM: Number(payload.raceIntervalAM ?? nextTimingSettings.raceIntervalAM),
                raceIntervalPM: Number(payload.raceIntervalPM ?? nextTimingSettings.raceIntervalPM),
                racesBetweenRounds: Number(payload.racesBetweenRounds ?? nextTimingSettings.racesBetweenRounds),
                spareRaceInterval: Number(payload.spareRaceInterval ?? nextTimingSettings.spareRaceInterval)
            });
            await fetchSettingsRows();
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update timing settings.');
            await fetchSettingsRows();
        } finally {
            setSavingTiming(false);
        }
    };

    const saveBroeMeetingIdentifier = async () => {
        setSavingBroeIdentifier(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/broe-identifier', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ broeMeetingIdentifier })
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message || `Failed to update BROE meeting identifier (HTTP ${response.status}).`);
            }

            const payload = await response.json().catch(() => ({}));
            setBroeMeetingIdentifier(String(payload?.broeMeetingIdentifier || broeMeetingIdentifier).trim());
            await Promise.all([fetchSettingsRows(), refreshCompetition()]);
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update BROE meeting identifier.');
        } finally {
            setSavingBroeIdentifier(false);
        }
    };

    const saveUrlSlug = async () => {
        setSavingUrlSlug(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/meeting/url-slug', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ urlSlug: urlSlug.trim() || null })
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message || `Failed to update URL slug (HTTP ${response.status}).`);
            }

            const payload = await response.json().catch(() => ({}));
            setUrlSlug(String(payload?.urlSlug || '').trim());
            await fetchSettingsRows();
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to update URL slug.');
        } finally {
            setSavingUrlSlug(false);
        }
    };

    const emailAllClubAdmins = useCallback(async () => {
        setErrorMessage('');

        const emailByNormalized = new Map();
        entries.forEach((entry) => {
            const raw = String(entry?.preEventContactEmail || '').trim();
            if (!raw || !raw.includes('@')) return;
            const normalized = raw.toLowerCase();
            if (!emailByNormalized.has(normalized)) {
                emailByNormalized.set(normalized, raw);
            }
        });

        const recipients = Array.from(emailByNormalized.values());
        if (!recipients.length) {
            setErrorMessage('No pre-event contact email addresses were found in entries.');
            return;
        }

        const emailList = recipients.join('\n');

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(emailList);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = emailList;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            window.alert(`Copied ${recipients.length} email address${recipients.length === 1 ? '' : 'es'} to clipboard.`);
        } catch (err) {
            console.error(err);
            setErrorMessage('Unable to copy email addresses to clipboard.');
        }
    }, [entries]);

    // Scratched entries (status 4)
    const scratchedEntries = entries.filter(e => e.status === 4);

    const competitorOrderStats = useMemo(() => {
        const activeEntries = entries.filter((entry) => entry.status !== 4);
        const parsedNumbers = activeEntries
            .map((entry) => Number(entry?.competitorNo))
            .filter((number) => Number.isFinite(number) && number > 0);

        const coxedNumbers = parsedNumbers.filter((number) => number < 100);
        const coxlessNumbers = parsedNumbers.filter((number) => number >= 100);

        const coxedMin = coxedNumbers.length ? Math.min(...coxedNumbers) : null;
        const coxedMax = coxedNumbers.length ? Math.max(...coxedNumbers) : null;
        const coxlessMin = coxlessNumbers.length ? Math.min(...coxlessNumbers) : null;
        const coxlessMax = coxlessNumbers.length ? Math.max(...coxlessNumbers) : null;

        return {
            coxedRange: coxedMin !== null ? `${coxedMin}-${coxedMax}` : '--',
            coxlessRange: coxlessMin !== null ? `${coxlessMin}-${coxlessMax}` : '--',
        };
    }, [entries]);

    const totalNonWithdrawnSeats = useMemo(() => {
        return entries
            .filter((entry) => Number(entry?.status) !== 4)
            .reduce((sum, entry) => {
                const seats = Number(entry?.seats || 0);
                if (!Number.isFinite(seats) || seats <= 0) return sum;
                return sum + seats + (entry?.coxed ? 1 : 0);
            }, 0);
    }, [entries]);

    const submittedEntries = useMemo(
        () => entries.filter((entry) => Number(entry?.status) === 1),
        [entries]
    );

    const combinedMastersEvents = useMemo(() => {
        // Race numbers keyed by event id, drawn from the timetable.
        const raceNumbersByEventId = new Map();
        (Array.isArray(timetableRaces) ? timetableRaces : []).forEach((race) => {
            const eventId = Number(race?.event?.id || 0);
            const raceNo = String(race?.raceNo || '').trim();
            if (eventId <= 0 || !raceNo) return;
            if (!raceNumbersByEventId.has(eventId)) raceNumbersByEventId.set(eventId, new Set());
            raceNumbersByEventId.get(eventId).add(raceNo);
        });

        const byEvent = new Map();
        entries
            .filter((entry) => Number(entry?.status) !== 4)
            .forEach((entry) => {
                const eventName = String(entry?.eventName || '').trim();
                if (!eventName) return;

                if (!byEvent.has(eventName)) {
                    byEvent.set(eventName, {
                        eventName,
                        eventTitle: String(entry?.eventTitle || eventName).trim(),
                        eventIds: new Set(),
                        crewCountByCategory: {},
                        totalCrews: 0
                    });
                }

                const group = byEvent.get(eventName);
                group.totalCrews += 1;
                const eventId = Number(entry?.eventId || 0);
                if (eventId > 0) group.eventIds.add(eventId);
                const crewCategory = normalizeMastersCategoryLetter(entry?.submittedMastersCategory);
                if (crewCategory) {
                    group.crewCountByCategory[crewCategory] = (group.crewCountByCategory[crewCategory] || 0) + 1;
                }
            });

        return [...byEvent.values()]
            .map((group) => {
                const raceNumbers = [...group.eventIds]
                    .flatMap((eventId) => [...(raceNumbersByEventId.get(eventId) || [])])
                    .filter((value, index, all) => all.indexOf(value) === index)
                    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));

                return {
                    ...group,
                    raceNumbers,
                    // Categories are derived from the actual crews entered, not the event name.
                    declaredCategories: Object.keys(group.crewCountByCategory).sort(compareMastersCategoryLetters)
                };
            })
            .filter((group) => group.declaredCategories.length > 1) // combined = crews from more than one category
            .sort((a, b) => compareEventSummaryByEventName(
                { eventName: a.eventName },
                { eventName: b.eventName }
            ));
    }, [entries, timetableRaces]);

    const noCompetitionEntries = useMemo(() => {
        // Count submitted (status 1) and scratched (status 4) crews together: a scratched
        // crew still counts as a submitted crew, so an event that had two crews (one now
        // scratched) is not "no competition".
        const crewCountByEvent = entries.reduce((acc, entry) => {
            if (Number(entry?.status) !== 1 && Number(entry?.status) !== 4) return acc;
            const eventName = String(entry?.eventName || '').trim();
            if (!eventName) return acc;
            acc[eventName] = (acc[eventName] || 0) + 1;
            return acc;
        }, {});

        return submittedEntries
            .filter((entry) => {
                const eventName = String(entry?.eventName || '').trim();
                return eventName && crewCountByEvent[eventName] === 1;
            })
            .sort((a, b) => compareEventSummaryByEventName(
                { eventName: a?.eventName },
                { eventName: b?.eventName }
            ));
    }, [entries, submittedEntries]);

    const showNoCompetitionWidget = useMemo(() => {
        const meetingDateValue = currentMeeting?.regattaDate || currentMeeting?.meetingDate || currentMeeting?.competitionDate;
        const meetingDate = parseMeetingDateStart(meetingDateValue);
        if (!meetingDate) return true;

        const cutoffDate = new Date(meetingDate);
        cutoffDate.setDate(cutoffDate.getDate() - 3);
        const now = new Date();

        return now < cutoffDate;
    }, [currentMeeting]);

    const exportActions = [
        {
            key: 'draw-pdf',
            title: 'Draw PDF Export',
            toolbarLabel: 'Draw PDF',
            description: 'Open a dedicated draw-print surface and generate a print-ready PDF copy of the draw.',
            tag: 'Live',
            buttonLabel: 'Open Export',
            variantClass: 'btn-primary',
            onClick: openDrawPdfExport,
            disabled: false
        },
        {
            key: 'timetable-standard',
            title: 'Timetable Excel',
            toolbarLabel: 'Timetable XLSX',
            description: 'Standard timetable workbook for circulation and operational review.',
            tag: 'Live',
            buttonLabel: 'Download Standard',
            variantClass: 'btn-outline-primary',
            onClick: () => downloadTimetableExport('standard'),
            disabled: loading
        },
        {
            key: 'timetable-officials',
            title: 'Timetable Excel Plus',
            toolbarLabel: 'Timetable+ XLSX',
            description: 'Double-height timetable rows with winner, verdict, and time columns for race-day annotation.',
            tag: 'Live',
            buttonLabel: 'Download Officials',
            variantClass: 'btn-outline-primary',
            onClick: () => downloadTimetableExport('officials'),
            disabled: loading
        },
        {
            key: 'race-cards',
            title: 'Race Cards Export',
            toolbarLabel: 'Race Cards',
            description: 'Open the dedicated race card export surface for printable race sheets.',
            tag: 'Live',
            buttonLabel: 'Open Export',
            variantClass: 'btn-primary',
            onClick: printRaceCards,
            disabled: false
        },
        {
            key: 'timetable-print-programme',
            title: 'Print Programme Timetable',
            toolbarLabel: 'Programme TSV',
            description: 'Tab-separated timetable file for print-programme preparation.',
            tag: 'Live',
            buttonLabel: 'Download TSV',
            variantClass: 'btn-outline-secondary',
            onClick: downloadProgrammeTimetableExport,
            disabled: loading
        },
        {
            key: 'broe-results',
            title: 'BROE Results File',
            toolbarLabel: 'BROE Results File',
            description: 'Generate the BROE-compatible results CSV from the current race data.',
            tag: 'Live',
            buttonLabel: 'Download CSV',
            variantClass: 'btn-info',
            onClick: downloadBroeResults,
            disabled: loading
        }
    ];

    const timingOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const spareRaceIntervalOptions = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 0];

    return (
        <div className='competition-admin-page'>
            <div className='competition-admin-hero'>
                <div>
                    <h1>{meetingTitle}</h1>
                    {currentMeeting?.broeRegattaStatus && (
                        <p className='ca-meeting-status'>{currentMeeting.broeRegattaStatus}</p>
                    )}
                    {meetingSubtitle && <p>{meetingSubtitle}</p>}
                </div>
                {view === 'entries' && (
                    <div className='ca-hero-stats' aria-label='Entry statistics'>
                        <div
                            className='ca-hero-stat'
                            title='Live Crews counts crews with status 1 (Accepted) or status 4 (Scratched). Total crews includes all crews for the meeting.'
                        >
                            <span className='ca-hero-stat-value'>{competitionAdminStats.liveCrews}</span>
                            <span className='ca-hero-stat-text'>
                                <span className='ca-hero-stat-label'>Live Crews</span>
                                <span className='ca-hero-stat-detail'>{competitionAdminStats.totalCrews} total</span>
                            </span>
                        </div>
                        <div
                            className='ca-hero-stat'
                            title='Competitors counts unique membership numbers across non-withdrawn crews (status is not 4). A competitor entered multiple times is counted once; competitors without membership numbers are not included. The seats line is the total boat seats across those same non-withdrawn crews.'
                        >
                            <span className='ca-hero-stat-value'>{competitionAdminStats.nonWithdrawnUniqueCompetitors}</span>
                            <span className='ca-hero-stat-text'>
                                <span className='ca-hero-stat-label'>Competitors</span>
                                <span className='ca-hero-stat-detail'>{totalNonWithdrawnSeats} seats</span>
                            </span>
                        </div>
                        <div className='ca-hero-stat'>
                            <span className='ca-hero-stat-value'><TotalPrizesBox prizes={prizes} /></span>
                            <span className='ca-hero-stat-text'>
                                <span className='ca-hero-stat-label'>Prizes</span>
                                <span className='ca-hero-stat-detail'>required</span>
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {errorMessage ? (
                <div className='ca-inline-error'>
                    <Alert key='danger-top' variant='danger' style={{ marginBottom: 0 }}>
                        {errorMessage}
                    </Alert>
                </div>
            ) : null}

            {view === 'competition' && (
            <section className='competition-admin-overview'>
                <div className='ca-card'>
                    <div className='ca-card-header'>
                        <h2>Competition Settings</h2>
                        <p>Meeting-level identifiers for BROE and public URLs.</p>
                    </div>
                    <div className='ca-settings-list'>
                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>BROE Meeting Identifier</h3>
                                <p>Used for BROE import/export against this meeting.</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='ca-settings-inline ca-settings-inline-tight'>
                                    <input
                                        type='text'
                                        className='form-control form-control-sm ca-settings-input'
                                        value={broeMeetingIdentifier}
                                        disabled={savingBroeIdentifier || loading}
                                        onChange={(event) => setBroeMeetingIdentifier(event.target.value)}
                                        placeholder='Set BROE meeting identifier'
                                    />
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-outline-primary'
                                        disabled={savingBroeIdentifier || loading}
                                        onClick={saveBroeMeetingIdentifier}
                                    >
                                        {savingBroeIdentifier ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>URL Slug</h3>
                                <p>Friendly path for this meeting.</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='ca-settings-inline'>
                                    <div className='input-group ca-settings-slug-group'>
                                        <span className='input-group-text ca-settings-slug-affix'>/</span>
                                        <input
                                            type='text'
                                            className='form-control form-control-sm'
                                            value={urlSlug}
                                            disabled={savingUrlSlug || loading}
                                            onChange={(e) => setUrlSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                            onKeyDown={(e) => e.key === 'Enter' && saveUrlSlug()}
                                            placeholder='e.g. sar2026'
                                        />
                                        <span className='input-group-text ca-settings-slug-affix'>/competition-admin</span>
                                    </div>
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-outline-primary'
                                        disabled={savingUrlSlug || loading}
                                        onClick={saveUrlSlug}
                                    >
                                        {savingUrlSlug ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                                {urlSlug && (
                                    <div className='ca-status-note'>
                                        Friendly URL: <strong>/{urlSlug}/competition-admin</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='ca-card'>
                    <div className='ca-card-header'>
                        <h2>Access &amp; Permissions</h2>
                        <p>Control public page visibility and what can be changed.</p>
                    </div>
                    <div className='ca-settings-list'>
                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>Public Pages Lock</h3>
                                <p>{publicPagesLocked ? 'Public timetable and draw are locked.' : 'Public timetable and draw are visible.'}</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='form-check form-switch ca-public-pages-switch'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id='competition-admin-public-lock'
                                        checked={publicPagesLocked}
                                        disabled={savingPublicLock || loading}
                                        onChange={(event) => setPublicLock(event.target.checked)}
                                        aria-label='Lock public pages'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>Read-Only</h3>
                                <p>{readOnly ? 'Regatta is read-only. Race and race status changes are blocked.' : 'Race and race status changes are allowed.'}</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='form-check form-switch ca-public-pages-switch'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id='competition-admin-read-only'
                                        checked={readOnly}
                                        disabled={savingReadOnly || loading}
                                        onChange={(event) => setReadOnlyFlag(event.target.checked)}
                                        aria-label='Make regatta read-only'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>Draw Mode</h3>
                                <p>{drawModes.drawModeEnabled ? 'Running the draw is available for this meeting.' : 'Running the draw is disabled for this meeting.'}</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='form-check form-switch ca-public-pages-switch'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id='competition-admin-draw-mode'
                                        checked={drawModes.drawModeEnabled}
                                        disabled={savingDrawModes || loading}
                                        onChange={(event) => setDrawModeFlag('drawModeEnabled', event.target.checked)}
                                        aria-label='Enable draw mode'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='ca-settings-item'>
                            <div className='ca-settings-meta'>
                                <h3>ReDraw Mode</h3>
                                <p>{drawModes.reDrawModeEnabled ? 'Redrawing individual events is available.' : 'Redrawing individual events is disabled.'}</p>
                            </div>
                            <div className='ca-settings-control'>
                                <div className='form-check form-switch ca-public-pages-switch'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id='competition-admin-redraw-mode'
                                        checked={drawModes.reDrawModeEnabled}
                                        disabled={savingDrawModes || loading}
                                        onChange={(event) => setDrawModeFlag('reDrawModeEnabled', event.target.checked)}
                                        aria-label='Enable redraw mode'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='ca-card'>
                    <div className='ca-card-header'>
                        <h2>Draw and Timetable</h2>
                        <p>Run the draw, rebuild the timetable.</p>
                    </div>
                    <div className='ca-metric-grid'>
                        <div className='ca-metric-tile'>
                            <div className='ca-metric-label'>Race Count</div>
                            <div className='ca-metric-value'>{competitionAdminStats.raceCount}</div>
                            <div className='ca-metric-detail'>{competitionAdminStats.emptyRaces} empty races</div>
                        </div>
                        <div className='ca-metric-tile'>
                            <div className='ca-metric-label'>Racing Ends</div>
                            <div className='ca-metric-value'>{competitionAdminStats.raceLast || '--:--'}</div>
                            <div className='ca-metric-detail'>Starts at {competitionAdminStats.raceFirst || '--:--'}</div>
                        </div>
                        <div className='ca-metric-tile ca-metric-tile-span-2'>
                            <div className='ca-metric-label'>Competitor Numbers To Order</div>
                            <div className='ca-metric-detail'>
                                1-99 (coxed): {competitorOrderStats.coxedRange} x2
                            </div>
                            <div className='ca-metric-detail'>
                                100+ (coxless): {competitorOrderStats.coxlessRange}
                            </div>
                        </div>
                    </div>

                    <div className='ca-timing-grid'>
                        <div className='ca-timing-item'>
                            <label className='ca-timing-label' htmlFor='timing-race-interval-am'>Race Interval AM</label>
                            <select
                                id='timing-race-interval-am'
                                className='form-select form-select-sm ca-timing-select'
                                value={timingSettings.raceIntervalAM}
                                disabled={savingTiming || loading}
                                onChange={(event) => updateTimingField('raceIntervalAM', event.target.value)}
                            >
                                {timingOptions.map(option => <option key={`am-${option}`} value={option}>{option}</option>)}
                            </select>
                        </div>

                        <div className='ca-timing-item'>
                            <label className='ca-timing-label' htmlFor='timing-race-interval-pm'>Race Interval PM</label>
                            <select
                                id='timing-race-interval-pm'
                                className='form-select form-select-sm ca-timing-select'
                                value={timingSettings.raceIntervalPM}
                                disabled={savingTiming || loading}
                                onChange={(event) => updateTimingField('raceIntervalPM', event.target.value)}
                            >
                                {timingOptions.map(option => <option key={`pm-${option}`} value={option}>{option}</option>)}
                            </select>
                        </div>

                        <div className='ca-timing-item'>
                            <label className='ca-timing-label' htmlFor='timing-races-between-rounds'>Races Between Rounds</label>
                            <select
                                id='timing-races-between-rounds'
                                className='form-select form-select-sm ca-timing-select'
                                value={timingSettings.racesBetweenRounds}
                                disabled={savingTiming || loading}
                                onChange={(event) => updateTimingField('racesBetweenRounds', event.target.value)}
                            >
                                {timingOptions.map(option => <option key={`rounds-${option}`} value={option}>{option}</option>)}
                            </select>
                        </div>

                        <div className='ca-timing-item'>
                            <label className='ca-timing-label' htmlFor='timing-spare-race-interval'>Spare Race Interval</label>
                            <select
                                id='timing-spare-race-interval'
                                className='form-select form-select-sm ca-timing-select'
                                value={timingSettings.spareRaceInterval}
                                disabled={savingTiming || loading}
                                onChange={(event) => updateTimingField('spareRaceInterval', event.target.value)}
                            >
                                {spareRaceIntervalOptions.map(option => (
                                    <option key={`spare-${option}`} value={option}>
                                        {option === 0 ? 'Off' : option}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {savingTiming ? <div className='ca-timing-note'>Saving timing settings...</div> : null}

                    <div className='ca-action-row'>
                        <button className='btn btn-primary' onClick={runDraw} disabled={!RunDrawAvailable || runningDraw}>
                            {runningDraw ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Running Draw and Timetable...
                                </>
                            ) : (
                                'Run Draw and Timetable'
                            )}
                        </button>
                    </div>
                </div>
            </section>
            )}

            {view === 'entries' && (
            <>
            <section className='ca-card ca-export-toolbar-card'>
                <div className='ca-export-toolbar' role='toolbar' aria-label='Actions'>
                    <span className='ca-export-toolbar-label'>Actions</span>
                    <span className='ca-export-toolbar-divider' aria-hidden='true'></span>
                    <button
                        type='button'
                        className='btn btn-sm ca-export-toolbar-btn'
                        title='Import clubs, event definitions, entries and competitors from BROE.'
                        onClick={importCrews}
                        disabled={loading || importing}
                    >
                        {importing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Importing...
                            </>
                        ) : (
                            'Import Crews'
                        )}
                    </button>
                    <span className='ca-export-toolbar-divider' aria-hidden='true'></span>
                    {exportActions.map((action) => {
                        if (action.key === 'broe-results') return null;
                        if (TIMETABLE_EXPORT_KEYS.has(action.key)) {
                            if (action.key !== 'timetable-standard') return null;
                            const timetableExports = exportActions.filter(item => TIMETABLE_EXPORT_KEYS.has(item.key));
                            return (
                                <Dropdown as={ButtonGroup} key='timetable-exports'>
                                    <button
                                        type='button'
                                        className='btn btn-sm ca-export-toolbar-btn'
                                        title={`${action.title} — ${action.description}`}
                                        onClick={action.onClick}
                                        disabled={action.disabled}
                                    >
                                        {action.toolbarLabel}
                                    </button>
                                    <Dropdown.Toggle
                                        split
                                        variant=''
                                        className='btn btn-sm ca-export-toolbar-btn ca-export-toolbar-caret'
                                        disabled={loading}
                                        aria-label='More timetable exports'
                                    />
                                    <Dropdown.Menu>
                                        {timetableExports.map((item) => (
                                            <Dropdown.Item
                                                key={item.key}
                                                onClick={item.onClick}
                                                disabled={item.disabled}
                                                title={`${item.title} — ${item.description}`}
                                            >
                                                {item.toolbarLabel}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            );
                        }
                        return (
                            <button
                                key={action.key}
                                type='button'
                                className='btn btn-sm ca-export-toolbar-btn'
                                title={`${action.title} — ${action.description}`}
                                onClick={action.onClick}
                                disabled={action.disabled}
                            >
                                {action.toolbarLabel}
                            </button>
                        );
                    })}
                    <span className='ca-export-toolbar-divider' aria-hidden='true'></span>
                    <button
                        type='button'
                        className='btn btn-sm ca-export-toolbar-btn'
                        title='Copy the email addresses of all club admins to the clipboard.'
                        onClick={emailAllClubAdmins}
                        disabled={loading || importing}
                    >
                        Copy Club Admin Emails
                    </button>
                    <span className='ca-export-toolbar-divider' aria-hidden='true'></span>
                    {exportActions.filter((action) => action.key === 'broe-results').map((action) => (
                        <button
                            key={action.key}
                            type='button'
                            className='btn btn-sm ca-export-toolbar-btn'
                            title={`${action.title} — ${action.description}`}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            {action.toolbarLabel}
                        </button>
                    ))}
                </div>
            </section>

            <div className='ca-data-layout'>
                <div>
                    {selectedEntries.length > 0 && (
                        <div className='ca-filter-bar'>
                            <span className='ca-filter-label'>Filtered by event:</span>
                            {selectedEntries.map((eventName) => (
                                <span className='ca-filter-pill' key={eventName}>{eventName}</span>
                            ))}
                            <button className='btn btn-secondary btn-sm' onClick={clearFilter}>
                                Clear Filter
                            </button>
                        </div>
                    )}
                    <EntriesList
                        entries={filteredEntries}
                        sortKey={sortKey}
                        sortDirection={sortDirection}
                        setSort={setSort}
                        broeDateTicks={broeDateTicks}
                        meetingTitle={meetingTitle}
                    />
                </div>

                <div className='ca-sidebar-stack'>
                    {entriesClosed && (
                        <ScratchedEntriesList
                            entries={scratchedEntries}
                            allEntries={entries}
                            selectedNames={selectedEntries}
                            onSelect={handleSelectEntry}
                        />
                    )}
                    <div className='ca-side-grid'>
                        <SingleEntriesList
                            singleEntries={singleEntries}
                            selectedNames={selectedEntries}
                            onSelect={handleSelectEntry}
                        />
                        <StraightFinalsList
                            straightFinals={straightFinals}
                            selectedNames={selectedEntries}
                            onSelect={handleSelectEntry}
                        />
                    </div>
                    <CombinedMastersEventsList
                        events={combinedMastersEvents}
                        selectedNames={selectedEntries}
                        onSelect={handleSelectEntry}
                    />
                    {showNoCompetitionWidget && (
                        <NoCompetitionList
                            entries={noCompetitionEntries}
                            selectedNames={selectedEntries}
                            onSelect={handleSelectEntry}
                        />
                    )}
                </div>
            </div>
            </>
            )}

            <ToastContainer position="bottom-end" className="p-3">
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide bg="success">
                    <Toast.Header>
                        <strong className="me-auto">Success</strong>
                    </Toast.Header>
                    <Toast.Body><span style={{color:"white"}}>Crews Imported Successfully</span></Toast.Body>
                </Toast>
            </ToastContainer>
            <ToastContainer position="bottom-end" className="p-3">
                <Toast onClose={() => setShowToastDraw(false)} show={showToastDraw} delay={3000} autohide bg="success">
                    <Toast.Header>
                        <strong className="me-auto">Success</strong>
                    </Toast.Header>
                    <Toast.Body><span style={{ color: "white" }}>Draw Completed!</span></Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
}

export function EntryAdmin() {
    return <CompetitionAdmin view="entries" />;
}
